import * as i0 from '@angular/core';
import { Injectable, Component, Input, Pipe, NgModule } from '@angular/core';
import * as i2$1 from '@angular/platform-browser';
import * as i1 from '@angular/common/http';
import { titleize, underscore, capitalize } from 'inflection';
import { zhCnLocale, ukLocale, trLocale, svLocale, ruLocale, roLocale, ptBrLocale, plLocale, nlLocale, koLocale, jaLocale, itLocale, huLocale, hiLocale, frLocale, esLocale, enGbLocale, deLocale, daLocale, csLocale, caLocale, defineLocale } from 'ngx-bootstrap/chronos';
import * as i2 from 'ngx-bootstrap/datepicker';
import { Subject, map, lastValueFrom, catchError, of } from 'rxjs';
import * as i3 from '@angular/common';
import { CommonModule } from '@angular/common';

const Supported_Langs = [
    'ca-ES',
    'cs-CZ',
    'cy-GB',
    'da-DK',
    'de-DE',
    'el-GR',
    'en-CA',
    'en-GB',
    'en-US',
    'es-ES',
    'es-MX',
    'fr-CA',
    'fr-FR',
    'hi-IN',
    'hu-HU',
    'it-IT',
    'ja-JP',
    'ko-KR',
    'ms-MY',
    'nl-NL',
    'pl-PL',
    'pt-BR',
    'pt-PT',
    'ro-RO',
    'ru-RU',
    'sv-SE',
    'tr-TR',
    'uk-UA',
    'zh-CHS',
    'zh-CHT'
];
const isTest = navigator.userAgent.includes('Node.js') || navigator.userAgent.includes('jsdom');
const currentlyLoadedNamespaces = [];
let resourceBundles = {};
const nsLoading = {};
const keyMissing = {};
const types = [
    'text',
    'hdr',
    'subhdr',
    'btn',
    'link',
    'lbl',
    'notification',
    'help',
    'option'
];
const language$ = new Subject();
var TextTransform;
(function (TextTransform) {
    TextTransform[TextTransform["None"] = 0] = "None";
    TextTransform[TextTransform["SentenceCase"] = 1] = "SentenceCase";
    TextTransform[TextTransform["TitleCase"] = 2] = "TitleCase";
})(TextTransform || (TextTransform = {}));
class I18nService {
    static { this.nsLangOverride = {}; }
    static { this.i18nPrefix = '/assets/i18n/'; }
    static { this.CACHE_VERSION = ''; }
    constructor(http, app, localeService) {
        this.http = http;
        this.app = app;
        this.localeService = localeService;
        this.changed = false;
        this.language$ = language$;
        this.endpoint = 'https://localhost:51850/InsertMissing';
        this.LANG_KEY = 'yc-language';
        this.DEFAULT_LANG = 'en-US';
        this.KEY_SEPARATOR = /\:|\./;
        this.CONTEXT_SEPARATOR = '__';
        this.CONTEXT_REGEX = new RegExp(`${this.CONTEXT_SEPARATOR}(\\w+)${this.CONTEXT_SEPARATOR}`, 'g');
        this.DEFAULT_NAMESPACES = ['common', 'help', 'tours', 'GLOBAL'];
        this.WILL_INSERT_MISSING = location.hostname.includes('localhost');
        this.REP_MAP = {
            id: 'ID'
        };
        if (!this.language.includes('-')) {
            this.setLanguage(this.DEFAULT_LANG);
        }
        this.detectFormattingData();
    }
    get language() {
        let lng = localStorage.getItem(this.LANG_KEY);
        if (!lng || (lng === 'null')) {
            lng = this.DEFAULT_LANG;
        }
        return lng;
    }
    get simpleLanguage() {
        return this.language.split('-')[0];
    }
    /**
     * The formatting data for the current locale
     */
    get numberFormattingData() {
        return { ...this._numberFormattingData };
    }
    setNumberFormatter() {
        // use the JS Intl lib to get a number formatter
        this.numberFormat = new Intl.NumberFormat(this.adaptLangKeyForIntl(this.language));
    }
    detectFormattingData() {
        if (!this.numberFormat) {
            this.setNumberFormatter();
        }
        if ('formatToParts' in this.numberFormat) {
            const parts = this.formatToParts(1234567.89);
            // find the group (thousands separator) and the decimal
            const group = parts.find(part => part.type === 'group').value;
            const decimal = parts.find(part => part.type === 'decimal').value;
            // set on `this`
            this._numberFormattingData = {
                group,
                decimal
            };
        }
        else {
            this.detectFormattingDataFallback();
        }
    }
    detectFormattingDataFallback() {
        // should be '1,234.5'
        const formatted = this.numberFormat.format(1234.5);
        // should be ',.'
        const [group, decimal] = formatted.replace(/\d/g, '');
        this._numberFormattingData = {
            group,
            decimal
        };
    }
    formatToParts(num) {
        return 'formatToParts' in this.numberFormat ?
            this.numberFormat.formatToParts(num) :
            this.formatToPartsFallback(num);
    }
    formatToPartsFallback(num) {
        if (!this._numberFormattingData) {
            this.detectFormattingData();
        }
        const formatted = this.numberFormat.format(num);
        const groups = formatted.split(this._numberFormattingData.group);
        const lastGroup = groups.pop();
        const [lastGroupValue, decimalValue] = lastGroup.split(this._numberFormattingData.decimal);
        let tailParts = [{
                type: 'integer',
                value: lastGroupValue
            }];
        if (decimalValue) {
            tailParts = [...tailParts, {
                    type: 'decimal',
                    value: this._numberFormattingData.decimal
                }, {
                    type: 'fraction',
                    value: decimalValue
                }];
        }
        return groups.reduce((acc, group) => {
            return [
                ...acc,
                {
                    value: group,
                    type: 'integer'
                },
                {
                    value: this._numberFormattingData.group,
                    type: 'group'
                }
            ];
        }, []).concat(tailParts);
    }
    /**
     * Returns a number that is formatted using the ES Intl.NumberFormat (https://mzl.la/2Z1DQCr)
     *
     * @param num Number to be formatted
     * @param config Formatting configuration. Falls back to users locale if not passed in
     * @returns Locale specific or config specific formatted number string
     */
    formatNumber(num, config) {
        if (!config) {
            return this.numberFormat.format(num);
        }
        const parts = this.formatToParts(num);
        if (config.allowDecimal && config.requireDecimal) {
            let decimal = parts.find(part => {
                return part.type === 'decimal';
            });
            if (!decimal) {
                decimal = {
                    type: 'decimal',
                    value: config.decimal
                };
                parts.push(decimal);
            }
            let fraction = parts.find(part => {
                return part.type === 'fraction';
            });
            if (!fraction) {
                fraction = {
                    type: 'fraction',
                    value: ''
                };
                parts.push(fraction);
            }
            decimal.value = config.decimal;
            for (let i = 0; i < config.decimalLimit; i++) {
                if (!fraction.value[i]) {
                    fraction.value += '0';
                }
            }
        }
        return parts.map(part => {
            if (part.type === 'decimal') {
                part.value = config.decimal;
            }
            if (part.type === 'group') {
                part.value = config.thousands;
            }
            return part.value;
        }).join('');
    }
    fetchLangs() {
        return this.http.get('/api/Account/Manage/GetLocales')
            .pipe(map(result => result.data));
    }
    insertMissingKey(namespace, key, defaultValue) {
        if (resourceBundles[namespace]) {
            resourceBundles[namespace][key] = defaultValue;
        }
        if (this.WILL_INSERT_MISSING) {
            const endpoint = `${this.endpoint}?namespaceName=${namespace}&keyName=${encodeURIComponent(key)}&localeName=${this.DEFAULT_LANG}&translation=${encodeURIComponent(defaultValue)}`;
            return lastValueFrom(this.http.get(endpoint));
        }
        else {
            return Promise.resolve();
        }
    }
    getDefault(key, transform = TextTransform.None) {
        let tmp = key;
        let keyValue = key.split(this.KEY_SEPARATOR)[1];
        if (key.split(this.KEY_SEPARATOR)[1]) {
            if ((/^([A-Z]|_)+(?!:[a-z])/).test(keyValue)) {
                keyValue = keyValue.toLowerCase();
            }
            tmp = titleize(underscore(keyValue));
            const words = tmp.split(' ');
            if (types.includes(words[0].toLowerCase())) {
                tmp = words.slice(1).join(' ');
            }
        }
        // recase if necessary
        if (transform === TextTransform.SentenceCase) {
            tmp = capitalize(tmp);
            Object.keys(this.REP_MAP).forEach(k => {
                tmp = tmp.replace(new RegExp('\\b' + k + '\\b', 'gi'), this.REP_MAP[k]);
            });
        }
        return tmp;
    }
    async ensureTranslate(key, context, defaultValue) {
        const [namespaceName] = key.split(this.KEY_SEPARATOR);
        await this.namespaceReady(namespaceName);
        return this.translate(key, context, defaultValue);
    }
    translate(key, context = {}, defaultValue, transform = TextTransform.None) {
        if (key === defaultValue) {
            return defaultValue;
        }
        if (typeof (defaultValue) === 'undefined') {
            defaultValue = this.getDefault(key, transform);
        }
        const [namespaceName, keyName] = key.split(this.KEY_SEPARATOR);
        if (!namespaceName || !keyName) {
            return '';
        }
        if (!currentlyLoadedNamespaces.includes(namespaceName)) {
            currentlyLoadedNamespaces.push(namespaceName);
        }
        if (!this.hasResourceBundle(namespaceName)) {
            this.loadNamespaces(namespaceName)
                .then(() => isTest ? null : this.translate(key, context, defaultValue, transform));
        }
        const retrievedTranslation = this._translate(key, context, defaultValue);
        if ((resourceBundles[namespaceName] && !resourceBundles[namespaceName][keyName]) &&
            !keyMissing[key]) {
            keyMissing[key] = this.insertMissingKey(namespaceName, key.split(this.KEY_SEPARATOR)[1], defaultValue);
        }
        return retrievedTranslation;
    }
    _translate(key, context, defaultValue) {
        const [namespaceName, keyName] = key.split(this.KEY_SEPARATOR);
        const translation = (resourceBundles[namespaceName] || {})[keyName] || defaultValue;
        if (translation) {
            let temp = translation;
            (translation.match(this.CONTEXT_REGEX) || []).forEach((match) => {
                const contextKey = match.replace(new RegExp(this.CONTEXT_SEPARATOR, 'g'), '');
                temp = temp.replace(match, '' + (typeof (context[contextKey]) === 'undefined' ? contextKey : context[contextKey]));
            });
            return temp;
        }
        return '';
    }
    fetchNamespace(lang, ns) {
        return lastValueFrom(this.http.get(`${I18nService.i18nPrefix}${lang}/${ns}.json`).pipe(catchError(error => {
            if (error.status === 404) {
                return of({});
            }
            console.error(error);
            return of(error);
        })));
    }
    async getNamespace(lang, ns) {
        try {
            const r = await this.fetchNamespace(lang, ns);
            if (r) {
                resourceBundles[ns] = r;
                this.attemptCacheSet(ns, r);
                delete nsLoading[ns];
            }
            return r;
        }
        catch (e) {
            console.error(e);
            throw e;
        }
    }
    async loadNamespaces(ns) {
        const lang = I18nService.nsLangOverride[this.language] || this.language;
        const cached = this.attemptCacheGet(ns);
        if (cached) {
            resourceBundles[ns] = cached;
            return cached;
        }
        if (nsLoading[ns]) {
            return nsLoading[ns];
        }
        else {
            nsLoading[ns] = this.getNamespace(lang, ns);
            return nsLoading[ns];
        }
    }
    adaptLangKeyForIntl(locale) {
        if (locale === 'zh-CHS') {
            locale = 'zh-TW';
        }
        else if (locale === 'zh-CHT') {
            locale = 'zh-CN';
        }
        return locale;
    }
    adaptLangToGooglePlaces(lang) {
        switch (lang.toLowerCase()) {
            case 'zh-chs':
                return 'zh-cn';
            case 'zh-cht':
                return 'zh-tw';
            case 'fr-ca':
                // split not necessary here
                return 'fr-ca';
            case 'cy-gb':
                return 'en-gb';
            default:
                return lang.split('-')[0];
        }
    }
    async setLanguage(lang) {
        if (!lang || (lang === 'null')) {
            lang = this.DEFAULT_LANG;
        }
        this.setLocaleForDatepicker(lang);
        localStorage.setItem(this.LANG_KEY, lang);
        this.setNumberFormatter();
        this.detectFormattingData();
        this.changed = true;
        const loadedNamespaces = Object.keys(resourceBundles);
        resourceBundles = {};
        for (const ns of loadedNamespaces) {
            await this.namespaceReady(ns);
        }
        this.language$.next(lang);
        this.app.tick();
    }
    hasResourceBundle(ns) {
        return !!resourceBundles[ns];
    }
    setLocaleForDatepicker(lang) {
        let locale;
        switch (lang) {
            default:
            case 'en-US':
                break;
            case 'ca-ES':
                locale = caLocale;
                break;
            case 'cs-CZ':
                locale = csLocale;
                break;
            case 'da-DK':
                locale = daLocale;
                break;
            case 'de-DE':
                locale = deLocale;
                break;
            case 'en-GB':
            case 'en-CA':
            case 'ms-MY': // These locales are not supported by ngx-bootstrap, but follow a similar format to en-GB
            case 'cy-GB':
            case 'el-GR':
                locale = enGbLocale;
                break;
            case 'es-ES':
            case 'es-MX':
                locale = esLocale;
                break;
            case 'fr-CA':
                locale = frLocale;
                break;
            case 'fr-FR':
                locale = frLocale;
                break;
            case 'hi-IN':
                locale = hiLocale;
                break;
            case 'hu-HU':
                locale = huLocale;
                break;
            case 'it-IT':
                locale = itLocale;
                break;
            case 'ja-JP':
                locale = jaLocale;
                break;
            case 'ko-KR':
                locale = koLocale;
                break;
            case 'nl-NL':
                locale = nlLocale;
                break;
            case 'pl-PL':
                locale = plLocale;
                break;
            case 'pt-PT':
            case 'pt-BR':
                locale = ptBrLocale;
                break;
            case 'ro-RO':
                locale = roLocale;
                break;
            case 'ru-RU':
                locale = ruLocale;
                break;
            case 'sv-SE':
                locale = svLocale;
                break;
            case 'tr-TR':
                locale = trLocale;
                break;
            case 'uk-UA':
                locale = ukLocale;
                break;
            case 'zh-CHS':
            case 'zh-CHT':
                locale = zhCnLocale;
                break;
        }
        let adaptedLang = lang.toLowerCase();
        if (adaptedLang === 'en-us') {
            adaptedLang = 'en';
        }
        else {
            defineLocale(adaptedLang, locale);
        }
        this.localeService.use(adaptedLang);
    }
    async namespaceReady(ns = this.DEFAULT_NAMESPACES) {
        if (ns instanceof Array) {
            for (const namespace of ns) {
                await this.namespaceReady(namespace);
            }
        }
        else {
            if (!this.hasResourceBundle(ns)) {
                await this.loadNamespaces(ns);
            }
        }
    }
    /**
     * Adapts the browser local and sets it
     *
     * @param browserLocale: Locale from browser - navigater.language(s)
     * @returns the locale of the user based off the browser and if it was set
     */
    adaptAndSetBrowswerLocale(browserLocale) {
        switch (browserLocale) {
            case 'da':
                browserLocale = 'da-DK';
                break;
            case 'en':
                browserLocale = 'en-US';
                break;
            case 'es':
                browserLocale = 'es-ES';
                break;
            case 'fr':
                browserLocale = 'fr-FR';
                break;
            case 'ru':
                browserLocale = 'ru-RU';
                break;
            case 'cs':
                browserLocale = 'cs-CZ';
                break;
            case 'de':
                browserLocale = 'de-DE';
                break;
            case 'hu':
                browserLocale = 'hu-HU';
                break;
            case 'it':
                browserLocale = 'it-IT';
                break;
            case 'ja':
                browserLocale = 'ja-JP';
                break;
            case 'ko':
                browserLocale = 'ko-KR';
                break;
            case 'ms':
                browserLocale = 'ms-MY';
                break;
            case 'nl':
                browserLocale = 'nl-NL';
                break;
            case 'sv':
                browserLocale = 'sv-SE';
                break;
            case 'pl':
                browserLocale = 'pl-PL';
                break;
            case 'pt':
                browserLocale = 'pt-PT';
                break;
            case 'ro':
                browserLocale = 'ro-RO';
                break;
            case 'tr':
                browserLocale = 'tr-TR';
                break;
            case 'uk':
                browserLocale = 'uk-UA';
                break;
            case 'zh':
                browserLocale = 'zh-CHS';
                break;
        }
        const languageIsValid = this.isLangValid(browserLocale);
        const languageIsChanged = browserLocale !== this.language;
        if (languageIsValid && languageIsChanged) {
            this.setLanguage(browserLocale);
        }
        return {
            didSet: languageIsValid,
            locale: browserLocale
        };
    }
    isLangValid(lang) {
        return Supported_Langs.includes(lang);
    }
    // CACHING
    // return a key if we have a cache version set up
    getVersionKey() {
        return I18nService.CACHE_VERSION ?
            `yc_I18N_CACHE_VERSION` :
            '';
    }
    getCurrentVersion() {
        return I18nService.CACHE_VERSION ?
            `${I18nService.CACHE_VERSION}_${this.language}` :
            '';
    }
    // return a prefix if we have a cache version set up
    getCacheKeyPrefix() {
        return I18nService.CACHE_VERSION ?
            `yc_I18N_CACHE_STORE` :
            '';
    }
    getCacheKey(ns) {
        const prefix = this.getCacheKeyPrefix();
        return prefix ?
            `${prefix}_${ns}` :
            '';
    }
    // clear the cache if the app has been updated
    validateVersionCache() {
        const versionKey = this.getVersionKey();
        // if we have caching enabled
        if (versionKey) {
            // pull the latest cache version
            const existingCacheVersion = localStorage.getItem(versionKey);
            const currentVersion = this.getCurrentVersion();
            // if we stored a different version than what we have
            if (existingCacheVersion !== currentVersion) {
                // clear the cache and let the cache rebuild
                this.clearCache();
                localStorage.setItem(versionKey, currentVersion);
            }
        }
    }
    clearCache() {
        const prefix = this.getCacheKeyPrefix();
        // if we have caching enabled
        if (prefix) {
            // loop over all of the keys in localstorage
            Object.keys(localStorage)
                // if it has the same prefix
                .filter(key => key.startsWith(prefix))
                // remove it from localStorage
                .forEach(key => localStorage.removeItem(key));
        }
    }
    attemptCacheGet(nsName) {
        const cacheKey = this.getCacheKey(nsName);
        if (cacheKey) {
            this.validateVersionCache();
            const currentCache = localStorage.getItem(cacheKey);
            if (currentCache) {
                return JSON.parse(currentCache);
            }
        }
        return null;
    }
    attemptCacheSet(nsName, ns) {
        const cacheKey = this.getCacheKey(nsName);
        if (cacheKey) {
            this.validateVersionCache();
            localStorage.setItem(cacheKey, JSON.stringify(ns));
        }
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: I18nService, deps: [{ token: i1.HttpClient }, { token: i0.ApplicationRef }, { token: i2.BsLocaleService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    /** @nocollapse */ static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: I18nService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: I18nService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.HttpClient }, { type: i0.ApplicationRef }, { type: i2.BsLocaleService }]; } });

class I18nComponent {
    constructor(i18next, elementRef, cdr, sanitizer) {
        this.i18next = i18next;
        this.elementRef = elementRef;
        this.cdr = cdr;
        this.sanitizer = sanitizer;
        this.context = {};
        this._translatedText = '';
        this.subscription = i18next.language$.subscribe(() => {
            this.ngOnChanges();
        });
    }
    get namespace() {
        return this.key.split(this.i18next.KEY_SEPARATOR)[0];
    }
    unescapeHtml(string) {
        if (string) {
            const temp = document.createElement('div');
            temp.innerHTML = string;
            const result = temp.childNodes[0].nodeValue;
            temp.removeChild(temp.firstChild);
            return result;
        }
        return '';
    }
    async ngOnChanges() {
        if (!this.i18next.hasResourceBundle(this.namespace)) {
            await this.i18next.loadNamespaces(this.namespace);
        }
        this._setTranslatedText();
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    _setTranslatedText() {
        if (this.key === this.defaultValue) {
            this._translatedText = this.defaultValue;
            return;
        }
        this._translatedText = this.unescapeHtml(this.i18next.translate(this.key, this.context, this.defaultValue, this.transform));
        this.cdr.markForCheck();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: I18nComponent, deps: [{ token: I18nService }, { token: i0.ElementRef }, { token: i0.ChangeDetectorRef }, { token: i2$1.DomSanitizer }], target: i0.ɵɵFactoryTarget.Component }); }
    /** @nocollapse */ static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: I18nComponent, selector: "yc-i18n", inputs: { key: "key", defaultValue: "defaultValue", context: "context", transform: "transform" }, usesOnChanges: true, ngImport: i0, template: `<ng-container *ngIf="_translatedText">{{ _translatedText }}</ng-container>`, isInline: true, dependencies: [{ kind: "directive", type: i3.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: I18nComponent, decorators: [{
            type: Component,
            args: [{
                    template: `<ng-container *ngIf="_translatedText">{{ _translatedText }}</ng-container>`,
                    selector: 'yc-i18n'
                }]
        }], ctorParameters: function () { return [{ type: I18nService }, { type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: i2$1.DomSanitizer }]; }, propDecorators: { key: [{
                type: Input
            }], defaultValue: [{
                type: Input
            }], context: [{
                type: Input
            }], transform: [{
                type: Input
            }] } });

class I18nPipeGetDefault {
    constructor(i18n) {
        this.i18n = i18n;
    }
    transform(value) {
        return this.i18n.getDefault(value);
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: I18nPipeGetDefault, deps: [{ token: I18nService }], target: i0.ɵɵFactoryTarget.Pipe }); }
    /** @nocollapse */ static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: I18nPipeGetDefault, name: "ycI18nGetDefault" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: I18nPipeGetDefault, decorators: [{
            type: Pipe,
            args: [{
                    name: 'ycI18nGetDefault',
                    pure: true
                }]
        }], ctorParameters: function () { return [{ type: I18nService }]; } });

class I18nPipe {
    constructor(i18n, cdr) {
        this.i18n = i18n;
        this.cdr = cdr;
        // e.g. login:btnLogin
        this._input = '';
        this._hasReceived = false;
        this.subscription = i18n.language$.subscribe(async (lang) => {
            this._hasReceived = false;
            await this.load();
        });
    }
    get namespace() {
        return this._input.split(this.i18n.KEY_SEPARATOR)[0];
    }
    async load() {
        if (!!this.namespace) {
            await this.i18n.namespaceReady(this.namespace);
            this._setTranslatedText();
        }
    }
    needsUpdate() {
        return this._hasReceived;
    }
    _setTranslatedText() {
        this._value = this.i18n.translate(this._input, this._context, this._defaultValue, this._transform);
        this.cdr.markForCheck();
    }
    // @angular fires this on any change
    transform(i18nKey, defaultTranslatedValue, context, transform) {
        if (i18nKey === defaultTranslatedValue) {
            return defaultTranslatedValue;
        }
        this._input = i18nKey;
        this._context = context;
        this._defaultValue = defaultTranslatedValue;
        this._transform = transform;
        if (!this._value) {
            this.load();
        }
        else {
            this._hasReceived = true;
        }
        return this._value;
    }
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: I18nPipe, deps: [{ token: I18nService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Pipe }); }
    /** @nocollapse */ static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: I18nPipe, name: "ycI18n", pure: false }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: I18nPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'ycI18n',
                    pure: false
                }]
        }], ctorParameters: function () { return [{ type: I18nService }, { type: i0.ChangeDetectorRef }]; } });

class I18nModule {
    /** @nocollapse */ static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: I18nModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    /** @nocollapse */ static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: I18nModule, declarations: [I18nComponent,
            I18nPipe,
            I18nPipeGetDefault], imports: [CommonModule], exports: [I18nComponent,
            I18nPipe,
            I18nPipeGetDefault] }); }
    /** @nocollapse */ static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: I18nModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: I18nModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    declarations: [
                        I18nComponent,
                        I18nPipe,
                        I18nPipeGetDefault
                    ],
                    exports: [
                        I18nComponent,
                        I18nPipe,
                        I18nPipeGetDefault
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { I18nComponent, I18nModule, I18nPipe, I18nPipeGetDefault, I18nService, Supported_Langs, TextTransform };
